// src/hooks/useCancelRequestPolling.js

import { useState, useEffect } from 'react';
import { apiService } from '@/services/api';
import { useUser } from '@/contexts/UserContext';

/**
 * Hook to poll for updates to prescription cancel requests
 * @param {number} intervalMs - Polling interval in milliseconds
 * @returns {Object} Object containing lastPolled timestamp and refresh function
 */
export function useCancelRequestPolling(intervalMs = 60000) {
  const { user, isAuthenticated } = useUser();
  const [lastPolled, setLastPolled] = useState(null);

  // Only prescribers receive cancel requests. auth/me returns erx_active on the
  // user row; require an explicit true so an absent flag never polls.
  const canPrescribe = user?.erx_active === true;
  
  // Poll for cancel request updates
  const pollCancelRequests = async () => {
    // Only proceed if user is authenticated and can prescribe
    if (!isAuthenticated || !user?.id || !canPrescribe) return;
    
    try {
      
      // Simply fetch the latest cancel requests
      await apiService.getCancelRequests(user.id);
      
      // Update last polled timestamp
      setLastPolled(new Date());
      
    } catch (error) {
      console.error('Error polling for cancel requests:', error);
    }
  };
  
  // Set up polling interval
  useEffect(() => {
    // Skip polling if not authenticated or not a prescriber
    if (!isAuthenticated || !user?.id || !canPrescribe) return;
    
    // Poll immediately on mount
    pollCancelRequests();
    
    // Set up interval for subsequent polls
    const intervalId = setInterval(pollCancelRequests, intervalMs);
    
    // Clean up on unmount
    return () => clearInterval(intervalId);
  }, [user?.id, isAuthenticated, canPrescribe, intervalMs]);
  
  // Function to force an immediate poll
  const refresh = () => {
    if (isAuthenticated && user?.id && canPrescribe) {
      pollCancelRequests();
    }
  };
  
  return {
    lastPolled,
    refresh
  };
}